import { useState } from 'react';
import { useOptionalAuth } from '../hooks/useOptionalAuth';
import { api } from '../api/client';
import type { Municipality, MunicipalityDeleteConflict } from '../api/types';
import { hasBillingAdmin } from '../lib/jwtRoles';
import { Card, Alert, Badge, Button } from './ui';
import { WaterRestrictions } from './WaterRestrictions';
import { dayLabel, levelOn, LEVEL_LABELS, nextChange, todayKey } from '../lib/waterRestrictions';

// Adding/removing a municipality is a billing:admin-only operation (the API
// enforces it too); non-admins never see this panel.

function isConflict(body: unknown): body is MunicipalityDeleteConflict {
  return (
    !!body &&
    typeof body === 'object' &&
    (body as { blocked?: unknown }).blocked === true
  );
}

interface Props {
  municipalities: Municipality[];
  /** Parent reloads its shared municipality list after an add/remove. */
  onChanged: () => void;
}

export function MunicipalityManager({ municipalities, onChanged }: Props) {
  // useOptionalAuth (not useAuth) so this renders harmlessly outside an
  // AuthProvider — e.g. in page tests that don't wire auth. Returns null there
  // → not admin → panel hidden. In the app main.tsx supplies the real auth.
  const auth = useOptionalAuth();
  const isAdmin = hasBillingAdmin(auth?.user?.access_token);

  const [name, setName] = useState('');
  const [province, setProvince] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null); // '__add__' while adding
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A successful delete that left harmless orphans behind — reported so the
  // operator's "I removed it cleanly" model is accurate, not silently wrong.
  const [notice, setNotice] = useState<string | null>(null);
  // A blocked delete: which municipality, and the list of what still references it.
  const [conflict, setConflict] = useState<{ name: string; body: MunicipalityDeleteConflict } | null>(null);
  // Which municipality's water restriction entries are open (one at a time).
  const [openRestrictions, setOpenRestrictions] = useState<string | null>(null);

  // Nothing here is available to non-admins; the whole panel is hidden.
  if (!isAdmin) return null;

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busyId) return;
    setError(null);
    setConflict(null);
    setNotice(null);
    setBusyId('__add__');
    try {
      await api.createMunicipality(trimmed, province.trim() || undefined);
      setName('');
      setProvince('');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add municipality');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (m: Municipality) => {
    if (busyId) return;
    setError(null);
    setConflict(null);
    setNotice(null);
    setBusyId(m.id);
    try {
      const res = await api.deleteMunicipality(m.id);
      setPendingRemove(null);
      // Report what the delete cleaned up — the API detaches archived tariffs/
      // schedules and removes holiday rules to satisfy FK constraints. Each is a
      // distinct entity, so they are counted and named separately, never merged.
      const c = res.cleaned;
      const plural = (n: number, s: string) => `${n} ${s}${n === 1 ? '' : 's'}`;
      const cleaned: string[] = [];
      if (c?.detachedTariffs > 0) cleaned.push(`detached ${plural(c.detachedTariffs, 'archived tariff')}`);
      if (c?.detachedSchedules > 0) cleaned.push(`detached ${plural(c.detachedSchedules, 'legacy schedule')}`);
      if (c?.removedHolidayRules > 0) cleaned.push(`removed ${plural(c.removedHolidayRules, 'holiday rule')}`);
      if (cleaned.length > 0) {
        setNotice(`Removed “${m.name}” — also ${cleaned.join(', ')} (all harmless — never billed).`);
      }
      onChanged();
    } catch (err) {
      const body = (err as { body?: unknown }).body;
      const status = (err as { status?: number }).status;
      if (status === 409 && isConflict(body)) {
        // Live references still attached — show exactly what to detach.
        setConflict({ name: m.name, body });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to remove municipality');
      }
      setPendingRemove(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="shadow-sm p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-content">Manage Municipalities</h2>
        <span className="text-xs text-content-muted">Admin only</span>
      </div>

      {error && (
        <Alert tone="danger" className="mb-3">
          <p>{error}</p>
        </Alert>
      )}

      {notice && (
        <Alert tone="info" className="mb-3">
          <div className="flex items-start justify-between gap-3">
            <p>{notice}</p>
            <button onClick={() => setNotice(null)} aria-label="Dismiss" className="text-content-muted hover:opacity-70 shrink-0">✕</button>
          </div>
        </Alert>
      )}

      {conflict && (
        <Alert tone="warning" className="mb-3">
          <p className="font-medium">
            Can’t remove “{conflict.name}” — {conflict.body.blocker_count}{' '}
            {conflict.body.blocker_count === 1 ? 'item is' : 'items are'} still attached. Detach these first:
          </p>
          <ul className="mt-2 list-disc pl-5 text-sm space-y-0.5">
            {conflict.body.blockers.activeTariffs.map((r) => (
              <li key={`t-${r.id}`}>Tariff — {r.label}</li>
            ))}
            {conflict.body.blockers.activeSchedules.map((r) => (
              <li key={`s-${r.id}`}>Tariff schedule — {r.label}</li>
            ))}
            {conflict.body.blockers.booklets.map((r) => (
              <li key={`b-${r.id}`}>Booklet — {r.label}</li>
            ))}
            {(conflict.body.blockers.restrictions ?? []).map((r) => (
              <li key={`w-${r.id}`}>Water restriction: {r.label}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Existing municipalities */}
      {municipalities.length === 0 ? (
        <p className="text-sm text-content-muted mb-4">No municipalities yet. Add one below.</p>
      ) : (
        <ul className="mb-4 divide-y divide-border-token border border-border-token rounded-lg">
          {municipalities.map((m) => {
            const entries = m.water_restrictions ?? [];
            const today = todayKey();
            const active = levelOn(entries, today);
            const next = nextChange(entries, today);
            const open = openRestrictions === m.id;
            return (
              <li key={m.id} className="px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="text-sm text-content min-w-0 truncate">
                      {m.name}
                      {m.province && <span className="text-content-muted"> · {m.province}</span>}
                    </span>
                    {/* Display only: today in SAST. Bills take the level of their own period. */}
                    {active && <Badge tone="danger">{LEVEL_LABELS[active]}</Badge>}
                    {next && (
                      <Badge tone="warning" wrap>
                        {`${next.level ? LEVEL_LABELS[next.level] : 'Water Wise'} from ${dayLabel(next.from)}`}
                      </Badge>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {/* Steps aside while a remove is pending: at 375px the row has
                        ~281px, and Restrictions + Remove? + Confirm + Cancel need ~314px. */}
                    {pendingRemove !== m.id && (
                      <Button
                        variant="ghost" size="sm"
                        aria-expanded={open}
                        aria-label={`Restrictions for ${m.name}`}
                        onClick={() => setOpenRestrictions(open ? null : m.id)}
                      >
                        Restrictions
                      </Button>
                    )}
                    {pendingRemove === m.id ? (
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-content-muted">Remove?</span>
                        <Button variant="danger" size="sm" onClick={() => remove(m)} disabled={busyId === m.id}>
                          Confirm
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => setPendingRemove(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setError(null); setConflict(null); setNotice(null); setPendingRemove(m.id); }}
                        aria-label={`Remove ${m.name}`}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                {open && <WaterRestrictions municipality={m} onChanged={onChanged} />}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add form */}
      <form onSubmit={add} className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[10rem]">
          <label className="block text-xs font-medium text-content mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Drakenstein Municipality"
            className="w-full px-3 py-2 border border-border-token rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="flex-1 min-w-[8rem]">
          <label className="block text-xs font-medium text-content mb-1">Province (optional)</label>
          <input
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            placeholder="e.g. Western Cape"
            className="w-full px-3 py-2 border border-border-token rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <Button type="submit" size="sm" disabled={!name.trim() || busyId === '__add__'}>
          {busyId === '__add__' ? 'Adding…' : 'Add'}
        </Button>
      </form>
    </Card>
  );
}
